import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import AthleteForm from './AthleteForm';
import ConnectByIDForm from './ConnectByIDForm';
import AthleteChoiceModal from './AthleteChoiceModal';
import { athleteAPI } from '../../services/api';
import { Athlete, AthleteFormData } from '../../types/mongodb';
import { PageTitle } from '../CoachDashboard/styles';
import { Link, useLocation } from 'react-router-dom';
import { RecordsSection as BaseRecordsSection, RecordsTable, TableHeader, TableRow } from '../TrainingSessions/styles';
import PlanBadge from '../Common/PlanBadge';

// Custom centered table cell – desktop size matches Equipment list
const CenteredTableCell = styled.td`
  padding: 0.9rem 0.7rem 0.9rem 0;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  text-align: center;
  vertical-align: middle;
  min-height: 2.25rem;
  box-sizing: border-box;

  @media (min-width: 769px) {
    font-size: 0.7rem;
    padding: 0.25rem 0.4rem;
    min-height: 2.25rem;
  }

  @media (max-width: 768px) {
    font-size: var(--mobile-list-cell, 0.65rem);
    padding: 0.25rem 0.4rem;
    min-height: 2rem;
  }
`;

// Plan column cell: badge vertically centered with other columns
const PlanCell = styled(CenteredTableCell)`
  vertical-align: middle;
  & > div {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
  }
`;

const AthletesTableRow = styled(TableRow)`
  td {
    vertical-align: middle !important;
    height: 2.5rem;
    box-sizing: border-box;
  }
  @media (max-width: 768px) {
    td {
      height: 2.25rem;
    }
  }
`;

// Table header – desktop size matches Equipment list
const AthletesTableHeader = styled(TableHeader)`
  @media (min-width: 769px) {
    font-size: 0.65rem;
    padding: 0.25rem 0.4rem;
  }
`;

const AthletesContainer = styled.div`
  padding: var(--spacing-xl) 1rem var(--spacing-xl) 1rem;
  width: 100%;
  margin: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-md);
  border-bottom: 2px solid var(--accent-orange);

  @media (max-width: 768px) {
    margin-bottom: 0;
    padding-bottom: 0.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PageDescription = styled.p`
  font-size: 0.85rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  margin: 0;

  @media (max-width: 768px) {
    font-size: var(--mobile-paragraph, 0.75rem);
  }
`;

const RecordsSection = styled(BaseRecordsSection)`
  background: none;
  border-radius: 0;
  padding: 1.5rem;
  box-shadow: none;
  margin-top: 0;
  width: 100%;
  max-width: none;

  @media (max-width: 768px) {
    margin-top: 0;
    margin-left: -2rem;
    margin-right: -2rem;
    width: calc(100% + 4rem);
    box-sizing: border-box;
    padding: 1rem;
  }
`;

const AddNewAthleteButton = styled.button`
  background: var(--accent-orange);
  color: #fff;
  border: 2px solid var(--accent-orange);
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 800;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(255, 150, 0, 0.3);
  width: 100%;
  max-width: 320px;

  &:hover {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 150, 0, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

const MoreButton = styled.button`
  background: var(--accent-orange);
  color: #fff;
  border: 2px solid var(--accent-orange);
  border-radius: 0.5rem;
  padding: 0.25rem 0.6rem;
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;

  &:hover {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  background: #FFF5F5;
  border: 1px solid #FFE3E3;
  color: #E03131;
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-shadow: var(--shadow-sm);

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
  font-size: 1.1rem;
`;

type FormMode = 'choice' | 'create' | 'connect';

const Athletes: React.FC = () => {
  const location = useLocation();
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnectingByID, setIsConnectingByID] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const state = location.state as { openAddAthlete?: boolean } | null;
    if (state?.openAddAthlete) {
      setFormMode('choice');
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Check if table can scroll horizontally (mobile)
  useEffect(() => {
    if (!isMobile || !scrollContainerRef.current) return;
    const checkScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        const canScroll = container.scrollWidth > container.clientWidth;
        const isScrolledToEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
        setShowScrollIndicator(canScroll && !isScrolledToEnd);
      }
    };
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [isMobile, athletes]);

  // Load athletes from MongoDB
  useEffect(() => {
    const loadAthletes = async () => {
      try {
        setIsLoading(true);
        const data = await athleteAPI.getAll();
        setAthletes(data);
        setError(null);
      } catch (err) {
        setError('Failed to load athletes. Please try again.');
        console.error('Error loading athletes:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadAthletes();
  }, []);

  const handleAddAthlete = async (athlete: AthleteFormData) => {
    try {
      const newAthlete = await athleteAPI.create(athlete);
      setAthletes(prev => [newAthlete, ...prev]);
      setFormMode(null);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add athlete. Please try again.');
      console.error('Error adding athlete:', err);
    }
  };

  const handleConnectByID = async (athleteId: string) => {
    const trimmedId = athleteId.trim();
    if (!trimmedId) {
      setError('Please enter a valid athlete ID.');
      return;
    }
    try {
      setError(null);
      setIsConnectingByID(true);

      // Fetch the existing athlete by ID (created by athlete)
      const existingAthlete = await athleteAPI.getByIdForConnect(trimmedId);

      if (!existingAthlete) {
        setError('Athlete not found. Please check the ID and try again.');
        return;
      }

      // Add coach to athlete's coachIds array (link existing athlete, don't create duplicate)
      try {
        await athleteAPI.addCoach(trimmedId);
      } catch (addCoachErr: any) {
        const errorMessage = addCoachErr.response?.data?.message || addCoachErr.message || 'Failed to connect athlete.';
        setError(errorMessage);
        console.error('Error adding coach to athlete:', addCoachErr);
        return;
      }

      // NO transfer-data call needed - data already exists and is linked to this athlete._id
      // Equipment and sessions are already linked to this athlete._id

      // Reload athletes to get the updated data
      const updatedAthletes = await athleteAPI.getAll();
      setAthletes(updatedAthletes);
      setError(null);
      setFormMode(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to connect athlete. Please check the ID and try again.';
      setError(errorMessage);
      console.error('Error connecting athlete:', err);
    } finally {
      setIsConnectingByID(false);
    }
  };

  // Note: handleDeleteAthlete is currently unused but may be needed for future functionality

  if (isLoading) {
    return (
    <AthletesContainer>
      <Header>
        <HeaderContent>
          <PageTitle>Athletes</PageTitle>
          <PageDescription>Manage and view all your athletes' training records and performance metrics.</PageDescription>
        </HeaderContent>
      </Header>
      <LoadingMessage>Loading athletes...</LoadingMessage>
    </AthletesContainer>
    );
  }

  return (
    <AthletesContainer>
      <Header>
        <HeaderContent>
          <PageTitle>Athletes</PageTitle>
          <PageDescription>Manage and view all your athletes' training records and performance metrics.</PageDescription>
        </HeaderContent>
      </Header>

      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}

      <div style={{
        marginBottom: 0,
        ...(isMobile ? { marginTop: '1.25rem' } : {})
      }}>
        <AddNewAthleteButton type="button" onClick={() => setFormMode('choice')}>
          Add new athlete
        </AddNewAthleteButton>
      </div>

      <RecordsSection>
        <div style={{
          fontWeight: 900,
          fontSize: isMobile ? 'var(--mobile-section-title, 1rem)' : '1.4rem',
          marginBottom: isMobile ? '1rem' : '2rem',
          color: 'var(--accent-orange)',
          fontFamily: "'Poppins', sans-serif",
          position: 'relative',
          paddingLeft: '1rem',
          letterSpacing: '-0.02em'
        }}>
          <span style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '6px',
            height: '100%',
            background: 'var(--accent-orange)'
          }}></span>
          Athlete Records:
        </div>
        <div
          ref={scrollContainerRef}
          style={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
            position: 'relative',
            boxSizing: 'border-box',
            ...(isMobile ? {
              marginLeft: '-1rem',
              marginRight: '-1rem',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            } : {})
          }}
        >
          <RecordsTable style={{
            fontSize: '0.75rem',
            width: '100%',
            tableLayout: isMobile ? 'auto' : 'fixed',
            minWidth: isMobile ? '520px' : 'auto'
          }}>
            <thead>
              <tr>
                <AthletesTableHeader style={{ fontWeight: 'bold', textAlign: 'center' }}>Athlete</AthletesTableHeader>
                <AthletesTableHeader style={{ fontWeight: 'bold', textAlign: 'center' }}>Plan</AthletesTableHeader>
                <AthletesTableHeader style={{ fontWeight: 'bold', textAlign: 'center' }}>Number of Jumps</AthletesTableHeader>
                <AthletesTableHeader style={{ fontWeight: 'bold', textAlign: 'center' }}>Average Distance</AthletesTableHeader>
                <AthletesTableHeader style={{ fontWeight: 'bold', textAlign: 'center' }}>Training Sessions</AthletesTableHeader>
                <AthletesTableHeader style={{ fontWeight: 'bold', textAlign: 'center' }}>More Info</AthletesTableHeader>
              </tr>
            </thead>
            <tbody>
              {athletes.map((athlete) => (
                <AthletesTableRow key={athlete._id}>
                  <CenteredTableCell>
                  <Link to={`/coach/athletes/${athlete._id}`} style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}>
                    {`${athlete.firstName} ${athlete.lastName}`}
                  </Link>
                </CenteredTableCell>
                  <PlanCell>
                    <div>
                      <PlanBadge plan={athlete.subscription?.plan || 'free'} size="small" />
                    </div>
                  </PlanCell>
                  <CenteredTableCell>{athlete.numberOfJumps || 0}</CenteredTableCell>
                  <CenteredTableCell>{athlete.averageDistance ? `${athlete.averageDistance}m` : '0m'}</CenteredTableCell>
                  <CenteredTableCell>{athlete.numberOfTrainingSessions || 0}</CenteredTableCell>
                  <CenteredTableCell><Link to={`/coach/athletes/${athlete._id}`}><MoreButton>More</MoreButton></Link></CenteredTableCell>
                </AthletesTableRow>
              ))}
            </tbody>
          </RecordsTable>
        </div>
        {isMobile && showScrollIndicator && (
          <div style={{
            fontSize: '0.65rem',
            color: '#000',
            fontFamily: "'Poppins', sans-serif",
            marginTop: '1rem',
            marginBottom: '0.5rem',
            paddingLeft: '1rem',
            fontWeight: 400
          }}>
            Scroll sideways to see all data
          </div>
        )}
      </RecordsSection>

      {formMode === 'choice' && (
        <AthleteChoiceModal
          onClose={() => setFormMode(null)}
          onSelectCreateNew={() => setFormMode('create')}
          onSelectConnectByID={() => setFormMode('connect')}
        />
      )}

      {formMode === 'create' && (
        <AthleteForm
          onClose={() => setFormMode(null)}
          onSubmit={handleAddAthlete}
          hideAthleteId={true}
        />
      )}

      {formMode === 'connect' && (
        <ConnectByIDForm
          onClose={() => {
            setFormMode(null);
            setError(null);
          }}
          onSubmit={handleConnectByID}
          error={error}
          isConnecting={isConnectingByID}
        />
      )}
    </AthletesContainer>
  );
};

export default Athletes; 